import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Star, Gift, Heart, Clock, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppSumoReviewPrompt = ({ onClose }) => {
  const [open, setOpen] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkReviewStatus();
  }, []);

  const checkReviewStatus = async () => {
    try {
      const token = localStorage.getItem('issuer_token');
      const issuerData = JSON.parse(localStorage.getItem('issuer_data') || '{}');
      
      if (!token || !issuerData.id) return;

      const response = await axios.get(`${API}/appsumo/review-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Issuer-ID': issuerData.id
        }
      });

      if (response.data.should_show_review) {
        setReviewData(response.data);
        setOpen(true);
      }
    } catch (err) {
      console.error('Failed to check review status:', err);
    }
  };

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('issuer_token');
      const issuerData = JSON.parse(localStorage.getItem('issuer_data') || '{}');

      await axios.post(`${API}/appsumo/review-action?action=${action}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Issuer-ID': issuerData.id
        }
      });

      if (action === 'completed') {
        // Open review URL in new tab
        window.open(reviewData.review_url, '_blank');
      }

      setOpen(false);
      if (onClose) onClose();
    } catch (err) {
      console.error('Review action error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!reviewData) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <Gift className="h-10 w-10 text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-md">
                <Heart className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>
          <DialogTitle className="text-center text-2xl">
            You're doing amazing! 🎉
          </DialogTitle>
          <DialogDescription className="text-center text-base mt-2">
            You've issued <span className="font-semibold text-amber-600">{reviewData.certificates_issued} certificates</span> with your {reviewData.tier_name} plan!
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 mb-4">
            <p className="text-slate-700 text-center leading-relaxed">
              We'd love your feedback! A quick review helps other course creators discover Certificate Machine on AppSumo.
            </p>
          </div>

          {/* Star Rating Visual */}
          <div className="flex justify-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className="h-8 w-8 text-amber-400 fill-amber-400 transition-transform hover:scale-110"
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            onClick={() => handleAction('completed')}
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-3 rounded-lg shadow-md"
            data-testid="leave-review-btn"
          >
            <Star className="h-5 w-5 mr-2" />
            Leave a Review on AppSumo
          </Button>
          
          <div className="flex gap-2">
            <Button
              onClick={() => handleAction('remind_later')}
              disabled={loading}
              variant="outline"
              className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg py-2.5"
              data-testid="remind-later-btn"
            >
              <Clock className="h-4 w-4 mr-1.5" />
              Remind Me Later
            </Button>
            <Button
              onClick={() => handleAction('dismiss')}
              disabled={loading}
              variant="ghost"
              className="flex-1 text-slate-500 hover:bg-slate-100 rounded-lg py-2.5"
              data-testid="dismiss-review-btn"
            >
              <X className="h-4 w-4 mr-1.5" />
              No Thanks
            </Button>
          </div>
        </div>

        <p className="text-xs text-slate-400 text-center mt-3">
          Your review helps us grow and improve the product for everyone!
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default AppSumoReviewPrompt;
